import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  beautyAssignableRolesApi,
  beautyCreateAccountApi,
  beautyLinkAccountApi,
  beautyPdvPointsApi,
  beautyProfessionalPdvPointsApi,
  beautyUnlinkAccountApi,
  beautyUpdateProfessionalPdvPointsApi,
} from "../../api/organizerBeautyApi";
import { ConfirmModal } from "./TurnosUi";
import LocalesPdv from "./LocalesPdv";
import "./turnos-beauty.css";
import "../events/EventForm.css";
import "../reservas/reservas-organizer.css";

export const MSG_NO_ENCONTRADA =
  "No encontramos una cuenta de tu negocio con ese usuario o correo. Revisá que esté bien escrito y que la persona sea parte de tu equipo en el portal de ZOCO.";
export const MSG_SOLO_DUENO = "Solo el dueño del negocio puede vincular cuentas.";
export const MSG_SOLO_DUENO_CREAR = "Solo el dueño del negocio puede crear cuentas.";
export const MSG_SIN_ROLES = "No hay roles activos en tu negocio. Creá uno en el portal de ZOCO.";
export const MSG_LOCALES_SIN_PERMISO =
  "No podés asignar locales a esta persona. Solo el dueño puede hacerlo, y la persona tiene que ser miembro activo del negocio.";
const MSG_PDV_NO_DISPONIBLE = "El PDV no está disponible por ahora. Probá de nuevo en unos minutos.";
const MIN_PASSWORD = 8;

// Maps a link failure to what the person sees; never says whether the account exists.
const mensajeDeVinculo = (e) => {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message;
  if (status === 404 && msg !== "Profesional no encontrado") return MSG_NO_ENCONTRADA;
  if (status === 403) return MSG_SOLO_DUENO;
  return msg || "No pudimos vincular la cuenta. Probá de nuevo.";
};

// Maps a create-account failure; the password is never part of any message.
const mensajeDeCreacion = (e) => {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message;
  if (status === 403) return msg && msg !== "Forbidden" ? msg : MSG_SOLO_DUENO_CREAR;
  return msg || "No pudimos crear la cuenta. Probá de nuevo.";
};

// Maps a failure loading/saving PDV locales; these never block creating the account.
const mensajeDeLocales = (e, fallback) => {
  const status = e?.response?.status;
  const data = e?.response?.data;
  if (status === 503 && data?.codigo === "pdv_no_disponible") return data.message || MSG_PDV_NO_DISPONIBLE;
  if (status === 403) return MSG_LOCALES_SIN_PERMISO;
  return data?.message || fallback;
};

const listaDeLocales = (data) => (Array.isArray(data?.info) ? data.info : []);
// The assigned puntos come as `{ puntosVenta }` or as a bare array.
const puntosAsignados = (info) => (Array.isArray(info) ? info : Array.isArray(info?.puntosVenta) ? info.puntosVenta : []);
const idDe = (p) => (p && typeof p === "object" ? p.id : p);
const mismosIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

/** Locales del PDV of a linked professional: shows the assigned ones and lets the owner change them. */
const LocalesAsignados = ({ profesionalId, onCambio }) => {
  const [estado, setEstado] = useState("loading"); // loading | error | ok
  const [errCarga, setErrCarga] = useState("");
  const [locales, setLocales] = useState([]);
  const [guardados, setGuardados] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errGuardar, setErrGuardar] = useState("");
  const [recienGuardado, setRecienGuardado] = useState(false);

  const cargar = useCallback(async () => {
    setEstado("loading");
    try {
      const [puntos, asignados] = await Promise.all([beautyPdvPointsApi(), beautyProfessionalPdvPointsApi(profesionalId)]);
      const activos = listaDeLocales(puntos.data);
      const asignadosLista = puntosAsignados(asignados.data?.info);
      const ids = asignadosLista.map(idDe);
      // An assigned punto missing from the active list still shows, checked.
      const faltantes = asignadosLista
        .filter((p) => !activos.some((l) => l.id === idDe(p)))
        .map((p) => ({ id: idDe(p), nombre: p?.nombre || idDe(p) }));
      setLocales([...activos, ...faltantes]);
      setGuardados(ids);
      setSeleccion(ids);
      setEstado("ok");
    } catch (e) {
      setErrCarga(mensajeDeLocales(e, "No pudimos cargar los locales."));
      setEstado("error");
    }
  }, [profesionalId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const toggle = (id) => {
    setRecienGuardado(false);
    setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const guardar = async () => {
    setErrGuardar("");
    setBusy(true);
    try {
      const { data } = await beautyUpdateProfessionalPdvPointsApi(profesionalId, seleccion);
      const ids = puntosAsignados(data?.info).map(idDe);
      setGuardados(ids);
      setSeleccion(ids);
      setRecienGuardado(true);
      onCambio?.("Locales actualizados");
    } catch (e) {
      setErrGuardar(mensajeDeLocales(e, "No pudimos guardar los locales. Probá de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  const hayCambio = !mismosIds(seleccion, guardados);
  // Says why the button is disabled: a gray button with no explanation looks broken.
  const estadoGuardado = busy ? "Guardando..." : hayCambio ? "" : recienGuardado ? "Guardado ✓" : "Sin cambios";

  return (
    <div className="zr-field" style={{ marginTop: 14 }}>
      <h5 className="zr-field__label" style={{ margin: 0 }}>Locales del PDV</h5>
      <LocalesPdv
        estado={estado}
        locales={locales}
        seleccionados={seleccion}
        onToggle={toggle}
        error={errCarga}
        onReintentar={cargar}
        disabled={busy}
      />
      {errGuardar && <div className="ef-alert" role="alert">{errGuardar}</div>}
      {estado === "ok" && locales.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
          <span className="zr-field__hint" role="status">
            {estadoGuardado}
          </span>
          <button type="button" className="zr-btn zr-btn--dark" disabled={!hayCambio || busy} onClick={guardar}>
            Guardar locales
          </button>
        </div>
      )}
    </div>
  );
};

/** "Acceso a ZOCO" of an existing professional: link an existing Zoco account by username or email or create a new one (owner only). */
const AccesoZoco = ({ profesional, salon, onCambio }) => {
  const esEmpleado = useSelector((s) => s.auth)?.user?.tipo === "empleado";
  const [linked, setLinked] = useState(Boolean(profesional.linked));
  const [identificador, setIdentificador] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [aDesvincular, setADesvincular] = useState(false);

  const [modo, setModo] = useState("vincular");
  const [nombre, setNombre] = useState(profesional.name || "");
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [verPassword, setVerPassword] = useState(false);
  const [rolId, setRolId] = useState("");
  const [roles, setRoles] = useState([]);
  const [rolesEstado, setRolesEstado] = useState("idle"); // idle | loading | error | ok
  const [errUsuario, setErrUsuario] = useState("");
  const [errCrear, setErrCrear] = useState("");
  const [sinVinculo, setSinVinculo] = useState("");
  const [locales, setLocales] = useState([]);
  const [localesEstado, setLocalesEstado] = useState("idle"); // idle | loading | error | ok
  const [errLocales, setErrLocales] = useState("");
  const [localesIds, setLocalesIds] = useState([]);
  const [avisoPdv, setAvisoPdv] = useState("");

  const cargarRoles = useCallback(async () => {
    setRolesEstado("loading");
    try {
      const { data } = await beautyAssignableRolesApi();
      const lista = Array.isArray(data?.info) ? data.info : [];
      setRoles(lista);
      setRolId((lista.find((r) => r.nombre === "Empleado") || lista[0])?.id || "");
      setRolesEstado("ok");
    } catch {
      setRolesEstado("error");
    }
  }, []);

  const cargarLocales = useCallback(async () => {
    setLocalesEstado("loading");
    try {
      const { data } = await beautyPdvPointsApi();
      setLocales(listaDeLocales(data));
      setLocalesEstado("ok");
    } catch (e) {
      setErrLocales(mensajeDeLocales(e, "No pudimos cargar los locales."));
      setLocalesEstado("error");
    }
  }, []);

  const elegirModo = (m) => {
    setModo(m);
    setError("");
    if (m === "crear" && rolesEstado === "idle") cargarRoles();
    if (m === "crear" && localesEstado === "idle") cargarLocales();
  };

  const toggleLocal = (id) => setLocalesIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const vincular = async () => {
    if (!identificador.trim()) {
      setError("Ingresá el usuario o el correo de la cuenta de Zoco");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await beautyLinkAccountApi(profesional._id, identificador.trim());
      setLinked(true);
      setIdentificador("");
      onCambio?.("Cuenta vinculada");
    } catch (e) {
      setError(mensajeDeVinculo(e));
    } finally {
      setBusy(false);
    }
  };

  const crearCuenta = async () => {
    setErrUsuario("");
    setErrCrear("");
    if (!nombre.trim() || !nombreUsuario.trim() || !password || !rolId) {
      setErrCrear("Completá nombre, usuario, contraseña y rol");
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setErrCrear(`La contraseña debe tener al menos ${MIN_PASSWORD} caracteres`);
      return;
    }
    setBusy(true);
    try {
      const res = await beautyCreateAccountApi({
        professionalId: profesional._id,
        nombre: nombre.trim(),
        nombreUsuario: nombreUsuario.trim(),
        password,
        rolId,
        ...(localesIds.length > 0 ? { puntosVentaIds: localesIds } : {}),
      });
      const asignacion = res?.data?.pdvAssignment ?? res?.data?.info?.pdvAssignment;
      if (asignacion?.ok === false) {
        setAvisoPdv(
          `La cuenta se creó, pero no pudimos asignar los locales${asignacion.message ? `: ${asignacion.message}` : ""}. Asignalos abajo.`
        );
      }
      setPassword("");
      setLinked(true);
      onCambio?.(`Cuenta creada. Usuario: ${nombreUsuario.trim()}`);
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 500 && data?.codigo === "cuenta_creada_sin_vinculo") {
        setPassword("");
        setSinVinculo(
          data.message || "La cuenta se creó en Zoco pero no se pudo vincular. Vinculala desde Equipo > Personas en el portal de ZOCO."
        );
      } else if (data?.codigo === "nombre_usuario_tomado") {
        setErrUsuario(data.message || "Ese nombre de usuario ya está en uso");
      } else {
        setErrCrear(mensajeDeCreacion(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const desvincular = async () => {
    setBusy(true);
    try {
      await beautyUnlinkAccountApi(profesional._id);
      setLinked(false);
      setADesvincular(false);
      onCambio?.("Cuenta desvinculada");
    } catch (e) {
      setADesvincular(false);
      setError(e?.response?.data?.message || "No pudimos desvincular la cuenta. Probá de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  const puedeCrear = !busy && !sinVinculo && rolesEstado === "ok" && Boolean(rolId);
  const nombreNegocio = salon?.name || "Tu salón";

  return (
    <div
      className="zr-panel"
      aria-label="Acceso a ZOCO"
      style={{ padding: 18, border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", marginTop: 20 }}
    >
      <div style={{ marginBottom: 14 }}>
        <h4 className="zr-field__label" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
          Acceso a ZOCO
        </h4>
        <p className="zr-field__hint" style={{ marginTop: 2, marginBottom: 0 }}>
          Con este usuario {profesional.name} entra al portal de ZOCO y al PDV, y en Agenda de turnos ve solo sus turnos (Mi agenda).
        </p>
      </div>

      {error && <div className="ef-alert" role="alert">{error}</div>}
      {sinVinculo && <div className="ef-alert" role="alert">{sinVinculo}</div>}
      {avisoPdv && linked && !esEmpleado && <div className="ef-alert" role="alert">{avisoPdv}</div>}

      {esEmpleado ? (
        <p className="ef-hint">{MSG_SOLO_DUENO}</p>
      ) : linked ? (
        <>
          <div className="bt-wrap-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 12, borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <span className="zr-badge zr-badge--present">
              <span className="dot" />Cuenta de ZOCO vinculada
            </span>
            <button type="button" className="zr-btn zr-btn--ghost" disabled={busy} onClick={() => setADesvincular(true)}>
              Desvincular
            </button>
          </div>
          <LocalesAsignados profesionalId={profesional._id} onCambio={onCambio} />
        </>
      ) : (
        <>
          <div className="pu-row-actions" role="group" aria-label="Cómo darle acceso" style={{ marginBottom: 16 }}>
            <button
              type="button"
              aria-pressed={modo === "vincular"}
              onClick={() => elegirModo("vincular")}
              className={`zr-btn ${modo === "vincular" ? "zr-btn--dark" : "zr-btn--ghost"}`}
            >
              Vincular una cuenta existente
            </button>
            <button
              type="button"
              aria-pressed={modo === "crear"}
              onClick={() => elegirModo("crear")}
              className={`zr-btn ${modo === "crear" ? "zr-btn--dark" : "zr-btn--ghost"}`}
            >
              Crear una cuenta nueva
            </button>
          </div>

          {modo === "vincular" ? (
            <div className="zr-field">
              <label className="zr-field__label">Usuario o correo de la cuenta de Zoco</label>
              <div className="bt-wrap-row" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="ef-input"
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label="Usuario o correo de la cuenta de Zoco"
                  value={identificador}
                  placeholder="usuario o correo@ejemplo.com"
                  onChange={(e) => setIdentificador(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button type="button" className="zr-btn zr-btn--dark" disabled={busy} onClick={vincular}>
                  Vincular
                </button>
              </div>
              <span className="zr-field__hint">
                Tiene que ser una persona de tu equipo en ZOCO. No hace falta que haya entrado antes a ZOCO Tickets.
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p className="ef-footnote" style={{ margin: 0 }}>
                La cuenta es de ZOCO y no hace falta verificar el correo. La persona entra con ese usuario y contraseña: compartísela vos.
              </p>
              {errCrear && <div className="ef-alert" role="alert">{errCrear}</div>}

              <div className="zr-field">
                <label className="zr-field__label" htmlFor="az-nombre">Nombre</label>
                <input
                  id="az-nombre"
                  className="ef-input"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </div>

              <div className="zr-field">
                <label className="zr-field__label" htmlFor="az-usuario">Nombre de usuario</label>
                <input
                  id="az-usuario"
                  className="ef-input"
                  autoComplete="off"
                  value={nombreUsuario}
                  placeholder="nombre.apellido"
                  onChange={(e) => setNombreUsuario(e.target.value)}
                />
                <span className="zr-field__hint">Con esto entra. Minúsculas, sin arroba y sin espacios.</span>
                {errUsuario && <div className="ef-hint" role="alert" style={{ color: "#e53935", fontWeight: 600 }}>{errUsuario}</div>}
              </div>

              <div className="zr-field">
                <label className="zr-field__label" htmlFor="az-password">Contraseña</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="az-password"
                    className="ef-input"
                    type={verPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    placeholder="Al menos 8 caracteres"
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="zr-btn zr-btn--ghost" onClick={() => setVerPassword((v) => !v)}>
                    {verPassword ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
                <span className="zr-field__hint">Al menos 8 caracteres. La escribís vos y se la pasás.</span>
              </div>

              <div className="zr-field">
                <label className="zr-field__label" htmlFor="az-rol">Rol</label>
                {rolesEstado === "loading" && <p className="ef-hint">Cargando roles...</p>}
                {rolesEstado === "error" && (
                  <div className="pu-row-actions">
                    <span className="ef-hint">No pudimos cargar los roles.</span>
                    <button type="button" className="zr-btn zr-btn--ghost" onClick={cargarRoles}>Reintentar</button>
                  </div>
                )}
                {rolesEstado === "ok" && roles.length === 0 && <p className="ef-hint">{MSG_SIN_ROLES}</p>}
                {rolesEstado === "ok" && roles.length > 0 && (
                  <select id="az-rol" className="ef-select" value={rolId} onChange={(e) => setRolId(e.target.value)}>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                  </select>
                )}
                <span className="zr-field__hint">
                  Profesional: entra a Agenda de turnos en el PDV y ve solo lo suyo.
                </span>
              </div>

              <div className="zr-field">
                <span className="zr-field__label">Locales del PDV donde trabaja</span>
                <LocalesPdv
                  estado={localesEstado}
                  locales={locales}
                  seleccionados={localesIds}
                  onToggle={toggleLocal}
                  error={errLocales}
                  onReintentar={cargarLocales}
                />
                <span className="zr-field__hint">Opcional. Si no elegís ninguno, no se asigna ningún local.</span>
              </div>

              <div className="zr-field">
                <label className="zr-field__label" htmlFor="az-negocio">Negocio</label>
                <input
                  id="az-negocio"
                  className="ef-input"
                  disabled
                  value={nombreNegocio}
                  style={{ background: "#f8fafc", color: "#64748b" }}
                />
              </div>

              <p className="ef-footnote" style={{ margin: 0 }}>
                Sin correo: no hay nada que verificar, el que responde por esta cuenta sos vos.
              </p>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="button" className="zr-btn zr-btn--dark" disabled={!puedeCrear} onClick={crearCuenta}>
                  Crear cuenta
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {aDesvincular && (
        <ConfirmModal title="¿Desvincular cuenta?" confirmLabel="Desvincular" busy={busy} onConfirm={desvincular} onCancel={() => setADesvincular(false)}>
          {profesional.name} va a dejar de ver su agenda en el panel.
        </ConfirmModal>
      )}
    </div>
  );
};

export default AccesoZoco;
